import type {ReactElement} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import styles from './control-items.module.scss';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {ActivityIndicator} from '@Components/activity-indicator/activity-indicator';
import {ActivityIndicatorSize, ActivityIndicatorType} from '@Components/activity-indicator/activity-indicator.types';

export interface ControlItemsProps {
  id: string;
  className?: string;
  icon: string;
  text?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  isSelected?: boolean;
  iconClasses?: string;
  textClasses?: string;
  wrapText?: boolean;
  hasCustomColor?: boolean;
  shouldShowToolTip?: boolean;
  tooltipText?: string;
  toolTipHideDelay?: number;
  showAsNewFeature?: boolean;

  onClick?(id: string): void;
}

export function ControlItems({
  className = '',
  text = '',
  onClick = (): void => {},
  isDisabled = false,
  isLoading = false,
  iconClasses = '',
  textClasses = '',
  isSelected = false,
  wrapText = false,
  hasCustomColor = false,
  ...props
}: ControlItemsProps): ReactElement<any> | null {
  const onClickHandler = (): void => {
    if (!isDisabled && !isLoading) {
      onClick(props.id);
    }
  };

  const getContainerClasses = (): string => {
    return [
      'flex-column-justify-center',
      'flex-items-center',
      'hover-transition-all',
      'spacing-p-b-0',
      styles.container,
      className,
      isDisabled ? styles.disabled : '',
      isSelected ? styles.selected : '',
      isLoading ? styles.loading : '',
    ].join(' ');
  };

  const getTextColorClass = (): string => {
    if (isDisabled) {
      return 'color-extra-light';
    }
    if (isSelected) {
      return 'content-primary';
    }

    if (hasCustomColor) {
      return '';
    }
    return 'content-sub-text';
  };

  const getControlItemsWithoutToolTip = (): ReactElement<any> => {
    return (
      <li id={props.id} onClick={onClickHandler} className={getContainerClasses()}>
        {!isLoading ? (
          <Icon isDisabled={isDisabled} className={iconClasses} icon={props.icon} size={IconSize.SIZE_ICON_24} />
        ) : (
          <CircularProgressLoader classes="spacing-m-l-0" size={LOADER_SIZE.SMALL} color={LOADER_COLOR.DARK} />
        )}
        <Text
          val={isLoading ? window.i18next.t('pmwjs_load') : text}
          size={TextSize.XXSMALL}
          className={`${styles.text} ${wrapText ? '' : styles.noWrapText} spacing-m-t-1 ${textClasses} ${getTextColorClass()}`}
        />
      </li>
    );
  };
  const getControlItemsWithToolTip = (): ReactElement<any> => {
    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        overlay={<Tooltip className={`spacing-m-b-1 _rounded-arrow ${styles.tooltip}`}>{props.tooltipText}</Tooltip>}
        delay={{show: 0, hide: props.toolTipHideDelay ?? 0}}
        placement="top"
      >
        {getControlItemsWithoutToolTip()}
      </OverlayTrigger>
    );
  };

  return props.shouldShowToolTip && props.tooltipText ? getControlItemsWithToolTip() : getControlItemsWithoutToolTip();
}
